import apiClient from './axiosConfig';
import type { AxiosError, AxiosResponse } from 'axios';
import { getMsalInstance, getApiRequest } from '../auth/authConfig';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
export type HttpRequestData = string | number | boolean | object | null;
export type HttpRequestParams = Record<string, string | number | boolean>;
export type ApiBaseType =
  | 'JAVA'
  | 'ZOOM'
  | 'VOYCE'
  | 'VERSION2'
  | 'INTEGRATION';

export type AuthType = 'ENTRA' | 'JWT' | 'NONE';

export interface HttpRequestConfig {
  url: string;
  method: HttpMethod;
  data?: HttpRequestData;
  params?: HttpRequestParams;
  headers?: Record<string, string>;
  apiBaseType?: ApiBaseType;
  baseUrls?: Partial<Record<ApiBaseType, string>>;
  authType?: AuthType;
  withCredentials?: boolean;
}

export interface ErrorResponse {
  status: number;
  message: string;
  code: number;
}

export interface CustomAxiosError extends AxiosError {
  response?: AxiosResponse<ErrorResponse>;
}

function createCustomError(
  status: number,
  message: string,
  data?: ErrorResponse
): Error {
  const error = new Error(message) as Error & {
    status: number;
    data?: ErrorResponse;
  };
  error.status = status;
  error.data = data;
  return error;
}

export const httpRequest = async <T>({
  url,
  method,
  data = null,
  params = {},
  headers = {},
  apiBaseType = 'JAVA',
  baseUrls = {},
  authType = 'ENTRA',
  withCredentials = false,
}: HttpRequestConfig): Promise<T> => {
  try {
    let accessToken: string | null = null;
    if (authType === 'ENTRA') {
      // Get msal instance from shared config
      const msalInstance = getMsalInstance();
      const apiRequest = getApiRequest();

      if (!msalInstance || !apiRequest) {
        throw createCustomError(500, 'MSAL is not initialized');
      }

      try {
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          const tokenResponse = await msalInstance.acquireTokenSilent({
            ...apiRequest,
            account: accounts[0],
          });
          accessToken = tokenResponse.accessToken;
        }
      } catch (silentError) {
        console.warn('Silent token failed, trying popup...', silentError);

        try {
          const tokenResponse =
            await msalInstance.acquireTokenPopup(apiRequest);
          accessToken = tokenResponse.accessToken;
        } catch (popupError) {
          console.error('Token popup failed', popupError);
        }
      }
    }
    const baseURL = baseUrls?.[apiBaseType];
    const authHeaders = accessToken
      ? { Authorization: `Bearer ${accessToken}` }
      : {};

    console.log('authHeaders', authHeaders);

    const response = await apiClient({
      baseURL,
      url,
      method,
      data,
      params,
      withCredentials,
      headers: {
        ...headers,
        ...authHeaders,
      },
    });
    return response.data;
  } catch (error) {
    const axiosError = error as CustomAxiosError;

    if (axiosError.response) {
      const { status, data } = axiosError.response;
      throw createCustomError(status, data?.message || 'API error', data);
    } else if (axiosError.request) {
      throw createCustomError(0, 'No response from server.');
    } else {
      throw createCustomError(0, axiosError.message || 'Request failed.');
    }
  }
};
